'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

const STORAGE_KEY = 'vc_prev_page_url';

// Tracks, per browser tab session, the full URL of the page a visitor was on
// immediately before their current page — so a submission on /contact-us can
// report which service/hire/technology page the user clicked through from.
//
// Dynamic by design: it never hardcodes a page slug or URL. It just records
// whatever `window.location.href` actually is on every route change, so it
// keeps working automatically through any future URL renames — nothing here
// needs updating when a page's URL changes.
//
// Renders nothing and only runs client-side, so it has zero effect on the
// server-rendered HTML, page weight, or SEO.
export default function PageJourneyTracker() {
    const pathname = usePathname();
    const lastUrl = useRef(null);

    useEffect(() => {
        if (lastUrl.current) {
            try {
                sessionStorage.setItem(STORAGE_KEY, lastUrl.current);
            } catch {
                // sessionStorage unavailable (private mode, etc.) — non-critical
            }
        }
        lastUrl.current = window.location.href;
        // Re-run on every route change so `lastUrl` always holds the page
        // being navigated FROM by the time the next change fires.
    }, [pathname]);

    return null;
}

/**
 * Read the tracked previous-page URL for use in a form payload. Falls back to
 * `document.referrer` (external sites, or a hard page load) when nothing has
 * been tracked yet in this tab session.
 */
export function getSourcePage() {
    if (typeof window === 'undefined') return '';
    try {
        return sessionStorage.getItem(STORAGE_KEY) || document.referrer || '';
    } catch {
        return document.referrer || '';
    }
}
